import type { Quizrun } from "@/types/domain";
import { parseTimeToSeconds } from "@/lib/utils";
import { isVirtualLocationId } from "@/lib/virtual";

export type EventStatus = "registration-open" | "in-progress" | "completed";

/**
 * Determine the status of a quizrun based on dates.
 */
export function getEventStatus(qr: Quizrun): EventStatus {
  const now = new Date();
  const startDate = new Date(qr.date_start);
  const endDate = new Date(qr.date_finish);

  if (endDate < now) return "completed";
  if (startDate <= now && now <= endDate) return "in-progress";
  return "registration-open";
}

/**
 * Per-event (per city/leg) status from its own start/end window. Each leg has
 * its own dates, so a quizrun can have Berlin live ("in-progress") while its
 * Virtual leg is still upcoming ("registration-open"). The quizrun-level
 * getEventStatus can't represent that — use this for city/leg rows.
 */
export function getEventStatusForDates(
  dateStart?: string,
  dateEnd?: string,
): EventStatus {
  const now = new Date();
  const start = dateStart ? new Date(dateStart) : null;
  // Legs may lack an explicit end; treat the start as a point-in-time window.
  const end = dateEnd ? new Date(dateEnd) : start;

  if (end && end < now) return "completed";
  if (start && start <= now && (!end || now <= end)) return "in-progress";
  return "registration-open";
}

/**
 * Whether an upcoming event *starts* within the next 7 days (and hasn't
 * started yet). Drives the per-leg "Almost here"/"Last chance" pill, where the
 * urgency is about the leg beginning soon (contrast isLastChance, which is
 * about a window *ending* soon).
 */
export function isStartingSoon(dateStart?: string): boolean {
  if (!dateStart) return false;
  const start = new Date(dateStart);
  const now = new Date();
  const msInWeek = 7 * 24 * 60 * 60 * 1000;
  return start.getTime() >= now.getTime() && start.getTime() - now.getTime() <= msInWeek;
}

/**
 * Whether an event ends within the next 7 days (and hasn't ended yet).
 * Drives the "Almost here" / "Last chance" urgency badges.
 */
export function isLastChance(dateFinish?: string): boolean {
  if (!dateFinish) return false;
  const end = new Date(dateFinish);
  const now = new Date();
  const msInWeek = 7 * 24 * 60 * 60 * 1000;
  return end.getTime() - now.getTime() <= msInWeek && end.getTime() >= now.getTime();
}

/**
 * Whether an event is in the past.
 */
export function isPastEvent(qr: Quizrun): boolean {
  return getEventStatus(qr) === "completed";
}

/**
 * Whether an event is upcoming (registration open or in progress).
 */
export function isUpcomingEvent(qr: Quizrun): boolean {
  return !isPastEvent(qr);
}

export interface WinnerInfo {
  onsite: { name: string; id?: number } | null;
  virtual: { name: string; id?: number } | null;
}

/**
 * Extract the winner from event results data.
 */
export function getWinner(results: Array<{
  totalScore?: string | null;
  format?: string | null;
  participant: string;
  personId?: number;
}>): WinnerInfo | null {
  if (results.length === 0) return null;

  // A sub-zero score is the *best* possible one, so only a missing/zero
  // placeholder score counts as "no score".
  const parseScore = (s: string | null | undefined): number => {
    if (!s || !s.trim()) return Infinity;
    const val = parseTimeToSeconds(s);
    return Number.isFinite(val) && val !== 0 ? val : Infinity;
  };

  const sorted = [...results].sort((a, b) => parseScore(a.totalScore) - parseScore(b.totalScore));

  const onsiteWinner = sorted.find(
    (r) => r.format === "Onsite" && parseScore(r.totalScore) < Infinity,
  );
  const virtualWinner = sorted.find(
    (r) => r.format === "Virtual" && parseScore(r.totalScore) < Infinity,
  );

  if (!onsiteWinner && !virtualWinner) return null;

  return {
    onsite: onsiteWinner
      ? { name: onsiteWinner.participant, id: onsiteWinner.personId }
      : null,
    virtual: virtualWinner
      ? { name: virtualWinner.participant, id: virtualWinner.personId }
      : null,
  };
}

/**
 * Count total participants including team members.
 */
export function getParticipantCount(results: Array<{
  teamMembers?: Array<unknown> | null;
}>): number {
  let count = 0;
  for (const result of results) {
    if (result.teamMembers && result.teamMembers.length > 0) {
      count += result.teamMembers.length;
    } else {
      count += 1;
    }
  }
  return count;
}


export interface OnsiteEventOption {
  eventId: number;
  locationId: number;
  /** City (+ venue), as the city itself reads on the results table. */
  label: string;
  /** Set only when the city has more than one edition, e.g. "31 Aug". */
  dateLabel?: string;
  dateStart: string;
}

/**
 * Onsite events a participant can be moved to, one row per EVENT.
 *
 * A quizrun can hold several events in the same city on different dates, and
 * those editions are genuinely different destinations, so they must stay
 * separately selectable. Options are therefore keyed by event id, not location:
 * keying by location gave two rows an identical value, which made the Select
 * mark every copy as selected. Only the repeated cities carry a date suffix, so
 * the common single-edition case stays uncluttered.
 */
export function onsiteEventOptions(
  events: Array<{
    id: number;
    location_id: number | null;
    date_start: string;
    location: { city: string; country: string; location_name?: string | null };
  }>,
): OnsiteEventOption[] {
  // A virtual event is not a real place.
  const onsite = events.filter((e) => !isVirtualLocationId(e.location_id));
  const perLocation = new Map<number, number>();
  for (const e of onsite) {
    perLocation.set(e.location_id, (perLocation.get(e.location_id) ?? 0) + 1);
  }

  return onsite
    .map((e) => ({
      eventId: e.id,
      locationId: e.location_id,
      label:
        `${e.location.city}, ${e.location.country}` +
        (e.location.location_name ? ` (${e.location.location_name})` : ""),
      // Only disambiguate where it is actually ambiguous.
      dateLabel:
        (perLocation.get(e.location_id) ?? 0) > 1
          ? formatShortDate(e.date_start)
          : undefined,
      dateStart: e.date_start,
    }))
    .sort(
      (a, b) =>
        a.label.localeCompare(b.label) ||
        new Date(a.dateStart).getTime() - new Date(b.dateStart).getTime(),
    );
}

/**
 * The results-table city filter. Identity is the real event_id (already on
 * every result row via get_event_registrations), not a home-grown encoding:
 * `eventId` names an exact edition, `null` means the whole city (or "all").
 */
export interface CityFilter {
  /** City name, or "all" for no filter. */
  city: string;
  /** Exact edition when a city repeats; null = whole city / all. */
  eventId: number | null;
}

/** No filter — every row passes. */
export const ALL_CITIES: CityFilter = { city: "all", eventId: null };

export interface CityFilterOption extends CityFilter {
  label: string;
}

/**
 * City filter options for the results table: one row per city, never per
 * edition. A city hosting the same quizrun twice still gets a single row —
 * picking it shows every edition, and the table's own date column says which
 * day a result came from. Dated rows made the list twice as long and read as
 * separate places.
 */
export function cityFilterOptions(
  results: Array<{ city: string; eventId?: number | null }>,
  _events: Array<{ id: number; date_start: string }>,
): CityFilterOption[] {
  const cities = new Set<string>();
  for (const r of results) cities.add(r.city);

  return [...cities]
    .sort()
    .map((city) => ({ label: city, city, eventId: null }));
}

/** Does a row belong to the selected city/edition? */
export function matchesCityFilter(
  result: { city: string; eventId?: number | null },
  filter: CityFilter,
): boolean {
  if (filter.city === "all") return true;
  if (filter.eventId === null) return result.city === filter.city;
  return result.eventId === filter.eventId;
}

/** Compact "31 Aug" used to tell repeated editions of a city apart. */
export function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/**
 * Cities that appear more than once in a list of events. Only these need a date
 * label to tell their editions apart; single-edition cities stay uncluttered.
 */
export function findRepeatedCities(
  events: Array<{ location: { city: string } }>,
): Set<string> {
  const seen = new Set<string>();
  const repeated = new Set<string>();
  for (const e of events) {
    if (seen.has(e.location.city)) repeated.add(e.location.city);
    seen.add(e.location.city);
  }
  return repeated;
}

/** Minimal event shape the gun-time / reassign helpers need. */
interface TargetableEvent {
  id: number;
  date_start: string;
  location: { city: string };
}

/**
 * Pick the event a host is most likely stamping right now: the one whose
 * scheduled start (date_start) is closest to now *today*, with a 5-min early
 * grace so a 10:00 event is already selected from 09:55. Returns undefined when
 * nothing starts around now — the host then picks their city explicitly rather
 * than us guessing an arbitrary event (e.g. viewing a future/past edition).
 */
function closestStartingEvent<T extends TargetableEvent>(
  events: T[],
): T | undefined {
  const now = Date.now();
  const graceMs = 5 * 60 * 1000;
  let best: T | undefined;
  let bestDelta = Infinity;
  for (const e of events) {
    const start = new Date(e.date_start).getTime();
    if (Number.isNaN(start)) continue;
    // Same calendar day as now, and not more than 5 min in the future.
    const sameDay =
      new Date(start).toDateString() === new Date(now).toDateString();
    if (!sameDay || start - now > graceMs) continue;
    const delta = Math.abs(now - start);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = e;
    }
  }
  return best;
}

/**
 * Which event the gun-time card acts on. A city can hold several events on
 * different dates, so an explicit in-card pick always wins: matching on city
 * name alone would pin whichever edition comes first in date order and silently
 * ignore the one the host just chose (and the one they are actually starting).
 */
export function resolveTargetEvent<T extends TargetableEvent>(
  manageableEvents: T[],
  { pickedId, cityFilter }: { pickedId: number | null; cityFilter?: CityFilter },
): T | undefined {
  if (manageableEvents.length === 1) return manageableEvents[0];

  const picked = manageableEvents.find((e) => e.id === pickedId);
  if (picked) return picked;

  if (cityFilter && cityFilter.city !== "all") {
    // The table filter can name an exact edition (eventId); honour it.
    if (cityFilter.eventId !== null) {
      const filtered = manageableEvents.find((e) => e.id === cityFilter.eventId);
      if (filtered) return filtered;
    }
    const inCity = manageableEvents.filter(
      (e) => e.location.city === cityFilter.city,
    );
    // Prefer the edition starting around now over the first in date order.
    const cityEvent = closestStartingEvent(inCity) ?? inCity[0];
    if (cityEvent) return cityEvent;
  }

  return closestStartingEvent(manageableEvents);
}
